import React from "react";
import { Modal, Button, Divider, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { roundDisplayPrice } from "../../../utils/priceHelpers";

const OrderDetailsModal = ({
  visible,
  order,
  onCancel,
  onCancelOrder,
  loading,
  tab,
  onIntervention,
}) => {
  if (!order) return null;

  const groupedItems =
    order?.orderItems?.reduce((acc, item) => {
      const deliveryType = item.deliveryDateType || "same_day";
      if (!acc[deliveryType]) {
        acc[deliveryType] = [];
      }
      acc[deliveryType].push(item);
      return acc;
    }, {}) || {};

  const renderOrderSection = (groupKey, items) => {
    const isNextDay = groupKey === "next_day";
    const deliveryDate = isNextDay
      ? order.scheduledDeliveryDate
        ? new Date(order.scheduledDeliveryDate).toLocaleDateString()
        : "TBD"
      : "Today";

    const sectionTitle = isNextDay
      ? `ORDER TO ${order.deliveryType === "DELIVERY" ? "DELIVER" : "PICKUP"
      } ON: ${deliveryDate}`
      : `ORDER TO ${order.deliveryType === "DELIVERY" ? "DELIVER" : "PICKUP"
      } TODAY`;

    const groupTotal = items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.price) * item.quantity;
      const containerCost = parseFloat(item.menuItem?.containerCost || 0);
      const portionsPerPack = item.menuItem?.portionsPerPack || 1;
      const numContainers = containerCost > 0 ? Math.ceil(item.quantity / portionsPerPack) : 0;
      return sum + itemTotal + (containerCost * numContainers);
    }, 0);

    return (
      <div
        key={groupKey}
        className="mb-8 pb-8 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0"
      >
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          {sectionTitle}
        </h3>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 font-semibold mb-1">Status:</p>
          <p className="text-lg font-bold text-gray-900">
            {order.status?.toUpperCase()}
          </p>
        </div>

        <div className="mb-6">
          <p className="text-lg font-semibold text-gray-800 mb-2">
            {order.deliveryType === "DELIVERY"
              ? "DELIVERY ADDRESS"
              : "PICKUP ADDRESS"}
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-gray-900">
              {order.deliveryType === "DELIVERY"
                ? order.delivery?.address ||
                order.savedAddress?.address ||
                "Customer Address"
                : order.restaurant?.businessInfo?.pickupAddress ||
                "Restaurant Address"}
            </p>
          </div>
        </div>

        {order.deliveryType === "DELIVERY" && (
          <div className="mb-6">
            <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <span className="text-lg font-semibold text-gray-800">
                Delivery Fee:
              </span>
              <span className="text-xl font-bold text-orange-600">
                ₦{parseFloat(order.deliveryFee).toFixed(2) || "0.00"}
              </span>
            </div>
          </div>
        )}

        {order.biker && order.deliveryType === "DELIVERY" && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="text-lg font-semibold text-purple-900 mb-3">
              🏍️ DELIVERY PERSON
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-purple-700 font-medium">Name:</span>
                <span className="ml-2 text-gray-900">
                  {order.biker?.user?.firstName} {order.biker?.user?.lastName}
                </span>
              </div>
              <div>
                <span className="text-purple-700 font-medium">Telephone:</span>
                <span className="ml-2 text-gray-900">
                  {order.biker?.user?.phoneCode}
                  {order.biker?.user?.phoneNumber || "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}

        {order.delivery?.trackerLink && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-blue-700 font-semibold mb-2">
              DELIVERY TRACKER / ETA:
            </p>
            <a
              href={order.delivery.trackerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              View Tracker
            </a>
          </div>
        )}

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-3">
            Order Items
          </h4>
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            {items.map((item, index) => {
              const containerCost = parseFloat(item.menuItem?.containerCost || 0);
              const portionsPerPack = item.menuItem?.portionsPerPack || 1;
              const numContainers = containerCost > 0 ? Math.ceil(item.quantity / portionsPerPack) : 0;
              const containerTotal = containerCost * numContainers;
              return (
                <div
                  key={index}
                  className="pb-3 border-b border-gray-200 last:border-b-0 last:pb-0"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="text-lg font-medium text-gray-900">
                        {item.quantity}x {item.menuItem?.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="small_currency">NGN</span>{" "}
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={""}
                          className="numeric"
                          displayType={"text"}
                          value={parseFloat(item.price).toFixed(2)}
                        />{" "}
                        per unit
                      </p>
                    </div>
                    <p className="text-lg font-bold text-[#44843F] ml-4">
                      <span className="small_currency">NGN</span>{" "}
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={""}
                        className="numeric"
                        displayType={"text"}
                        value={(parseFloat(item.price) * item.quantity).toFixed(2)}
                      />{" "}
                    </p>
                  </div>
                  {containerTotal > 0 && (
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-sm text-gray-600">
                        Container Cost (x{numContainers})
                      </p>
                      <p className="text-sm font-semibold text-[#44843F] ml-4">
                        <span className="small_currency">NGN</span>{" "}
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={""}
                          className="numeric"
                          displayType={"text"}
                          value={containerTotal.toFixed(2)}
                        />{" "}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border border-green-200">
          <span className="text-lg font-semibold text-gray-800">
            AMOUNT YOU WILL GET:
          </span>
          <span className="text-2xl font-bold text-green-600">
            <span className="small_currency">NGN</span>{" "}
            <NumberFormat
              thousandSeparator={true}
              prefix={""}
              className="numeric"
              displayType={"text"}
              value={roundDisplayPrice(groupTotal)}
            />{" "}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={`Order Details - #${order.orderCode}`}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
      closeIcon={<CloseOutlined />}
    >
      <div className="space-y-6" style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}>
        {/* Header Section */}
        <div className="space-y-4 pb-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-sm text-gray-600 font-semibold mb-1">
                Order Time:
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {new Date(order.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Business Information
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-blue-700 font-medium">Name:</span>
              <span className="ml-2 text-gray-900">
                {order.restaurant?.businessInfo?.name || "N/A"}
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Telephone:</span>
              <span className="ml-2 text-gray-900">
                {order.restaurant?.businessInfo?.phoneNumber || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Customer Information
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-blue-700 font-medium">Name:</span>
              <span className="ml-2 text-gray-900">
                {order.user?.firstName} {order.user?.lastName}
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Telephone:</span>
              <span className="ml-2 text-gray-900">
                {order.user?.phoneCode} {order.user?.phoneNumber}
              </span>
            </div>
          </div>
        </div>

        {/* Order Sections by Delivery Date Type */}
        <div>
          {Object.keys(groupedItems).map((groupKey) =>
            renderOrderSection(groupKey, groupedItems[groupKey]),
          )}
        </div>

        {onIntervention && !order.readOnly && (
          <>
            <Divider />
            <div className="flex gap-2">
              <Button
                block
                type="primary"
                danger
                onClick={onIntervention}
              >
                Take Intervention Action
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
